import React, { useContext } from "react";
import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import { useHistory } from "react-router-dom";
import ModifyCartPanel from "./ModifyCartPanel";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CartContext from "../../../contexts/CartContext";
import ConfigContext from "../../../contexts/ConfigContext";

const drawerWidth = 480;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      "& > *": {},
    },
    appBar: {},
    appBarShift: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
    menuButton: {},
    hide: {
      display: "none",
    },
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
    },
    drawerPaper: {
      width: drawerWidth,
    },
    drawerHeader: {
      display: "flex",
      alignItems: "center",
      // necessary for content to be below app bar
      justifyContent: "flex-end",
    },
    content: {
      flexGrow: 1,
      marginLeft: -drawerWidth,
    },
    contentShift: {
      marginLeft: 0,
    },
  }),
);

type Props = {
  cartOpen: boolean;
  setCartOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function CartDrawer({ cartOpen, setCartOpen }: Props) {
  const { config } = useContext(ConfigContext);
  const history = useHistory();
  const classes = useStyles();
  const { cart, setCartState } = useContext(CartContext);
  const disabled = config.general.site_disabled;

  const handleDrawerClose = () => {
    setCartOpen(false);
  };

  const handleCheckout = () => {
    setCartOpen(false);
    history.push("/order/checkout");
  };

  const itemCount = Object.values(cart.items).reduce((sum, item) => sum + item.count, 0);
  const hasItems = itemCount > 0;

  return (
    <Drawer
      className={classes.drawer + " drawer-button cart-drawer"}
      anchor="right"
      open={cartOpen}
      classes={{
        paper: classes.drawerPaper,
      }}
      onClose={handleDrawerClose}
    >
      <div className="cart-drawer-header">
        <div className="cart-drawer-title">
          <ShoppingCartIcon />
          Cart{itemCount > 0 && ` (${itemCount})`}
        </div>
        <IconButton onClick={handleDrawerClose} size="large">
          <CloseIcon />
        </IconButton>
      </div>

      <div className="cart-drawer-content">
        {disabled ? (
          <div style={{ padding: "1em" }}>
            Ordering from the site is temporarily disabled while we perform yearly
            inventory and restock. Please call <b>(902) 530-0420</b> to order by
            phone with one of our super helpful staff. Sorry for the
            inconvenience.
          </div>
        ) : (
          <ModifyCartPanel
            hideDiscount={true}
            hideTax={true}
            hideTotal={true}
            hideInactive={true}
            hideQuantity={true}
            hideShipping={!cart.deliveryAddress.postal}
          />
        )}
      </div>

      {!disabled && hasItems && (
        <div className="cart-drawer-footer">
          <Button
            variant="contained"
            color="primary"
            startIcon={<ShoppingCartCheckoutIcon />}
            onClick={handleCheckout}
          >
            Checkout
          </Button>
        </div>
      )}
    </Drawer>
  );
}
